import { collection, getDocs, query, where } from "firebase/firestore";
import { motion } from "framer-motion";
import { AlertCircle, Brain, Save, LogOut, CheckCircle } from "lucide-react";
import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { HarmonicGraph } from "../components/HarmonicGraph";
import { ResultsGraph } from "../components/ResultsGraph";
import { useAssessment } from "../contexts/AssessmentContext";
import { useResults } from "../contexts/ResultsContext";
import { auth, db } from "../lib/firebase";
import { LoadingSpinner } from "../components/LoadingSpinner";
import type { Score } from "../types";
import type { HarmonicScore } from "../types/harmonic";

export function ResultsView() {
  const navigate = useNavigate();
  const { scores, harmonicScores } = useAssessment();
  const { saveResults, isLoading, error } = useResults();
  const [hasAiAccess, setHasAiAccess] = useState<boolean | null>(null);
  const [isCheckingAccess, setIsCheckingAccess] = useState(true);
  const [isSaved, setIsSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const hasAutoSaved = useRef(false);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/login");
    } catch (err) {
      console.error("Error signing out:", err);
    }
  };

  const checkAccess = async () => {
    if (!auth.currentUser) return;

    try {
      const respondentQuery = query(
        collection(db, "respondents"),
        where("userId", "==", auth.currentUser.uid)
      );
      const respondentDocs = await getDocs(respondentQuery);

      if (!respondentDocs.empty) {
        const respondentData = respondentDocs.docs[0].data();
        const coachId = respondentData.coachId;

        if (coachId) {
          const coachQuery = query(
            collection(db, "coaches"),
            where("userId", "==", coachId)
          );
          const coachDocs = await getDocs(coachQuery);

          if (!coachDocs.empty) {
            const coachData = coachDocs.docs[0].data();
            setHasAiAccess(
              coachData.tier === "advanced" || coachData.tier === "partner"
            );
          } else {
            setHasAiAccess(false);
          }
        } else {
          setHasAiAccess(false);
        }
      } else {
        setHasAiAccess(false);
      }
    } catch (err) {
      console.error("Error checking access:", err);
      setHasAiAccess(false);
    } finally {
      setIsCheckingAccess(false);
    }
  };

  const handleSave = async () => {
    if (!auth.currentUser || !scores || !harmonicScores) return;

    setSaveError(null);
    try {
      await saveResults({
        userId: auth.currentUser.uid,
        scores,
        harmonicScores,
        completedAt: new Date(),
      });
      setIsSaved(true);
    } catch (err) {
      console.error("Error saving results:", err);
      setSaveError("Failed to save your results. Please try again.");
    }
  };

  useEffect(() => {
    checkAccess();
  }, [auth?.currentUser]);

  useEffect(() => {
    if (hasAutoSaved.current) return;
    if (!auth.currentUser || !scores || !harmonicScores) return;
    if (scores.length === 0 || harmonicScores.length === 0) return;

    hasAutoSaved.current = true;
    handleSave();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scores, harmonicScores]);

  if (!scores || !harmonicScores) {
    return <LoadingSpinner message="Loading assessment results..." />;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Brain className="w-16 h-16 text-blue-600" />
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">
                Your Personality Breakthrough Profile Results
              </h1>
              <p className="text-xl text-gray-600">
                Detailed analysis of your characteristics
              </p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-100 text-gray-700 
              hover:bg-gray-200 transition-all"
          >
            <LogOut className="w-5 h-5" />
            <span>Logout</span>
          </button>
        </div>

        {(error || saveError) && (
          <div className="flex items-center gap-2 bg-red-500/10 text-red-600 p-4 rounded-xl mb-6">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <p>{saveError || error}</p>
          </div>
        )}

        {isSaved && (
          <div className="flex items-center gap-2 bg-emerald-50 text-emerald-700 p-4 rounded-xl mb-6 border border-emerald-200">
            <CheckCircle className="w-5 h-5 flex-shrink-0" />
            <p>Your results have been saved successfully.</p>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-8 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-8 shadow-sm border border-green-200"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-8">
              Characteristic Profile
            </h2>
            <ResultsGraph scores={scores} />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-8 shadow-sm border border-purple-200"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-8">
              Harmonic Scale Profile
            </h2>
            <HarmonicGraph scores={harmonicScores} />
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="flex flex-col items-center gap-4"
        >
          {!isSaved && !saveError && (
            <button
              onClick={handleSave}
              disabled={isLoading}
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-blue-600 hover:bg-blue-700 
                text-white font-medium hover:scale-105 transition-all disabled:opacity-50"
            >
              <Save className="w-5 h-5" />
              {isLoading ? "Saving Results..." : "Save Results"}
            </button>
          )}

          {!isSaved && saveError && (
            <button
              onClick={handleSave}
              disabled={isLoading}
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-blue-600 hover:bg-blue-700 
                text-white font-medium hover:scale-105 transition-all disabled:opacity-50"
            >
              <Save className="w-5 h-5" />
              {isLoading ? "Saving Results..." : "Retry Save"}
            </button>
          )}

          {!isCheckingAccess && hasAiAccess && (
            <Link
              to="/analysis"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-blue-600 hover:bg-blue-700 
                text-white font-medium hover:scale-105 transition-all"
            >
              <Brain className="w-5 h-5" />
              Get AI Analysis
            </Link>
          )}

          {!isCheckingAccess && !hasAiAccess && (
            <div className="text-gray-500 text-center">
              AI Analysis is not available with your current plan. Please
              contact your coach for more information.
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
